/**
 * Money helpers — the branded `Cents` integer type + decimal.js-backed math.
 *
 * ── Canonical rounding mode (AUDIT-06 decision note) ─────────────────────────
 * Every helper that DIVIDES or ROUNDS a monetary value pins
 * `Decimal.ROUND_HALF_UP` EXPLICITLY as the single canonical mode for the
 * dollars↔Cents numeric boundary: {@link dollarsToCents}, {@link multiplyCents},
 * {@link divideCents}, {@link percentOfCents}, {@link applyBasisPoints}. HALF_UP
 * rounds a `.5` fraction away from zero (0.5→1, 2.5→3, −0.5→−1).
 *
 * WHY pinned, not the decimal.js global default: a `Decimal.set({ rounding })`
 * anywhere else in the process must NEVER be able to silently shift monetary
 * rounding and accrue cent-level drift in balances. The mode is part of the
 * function contract, not ambient configuration.
 *
 * WHY HALF_UP: it matches `Organization.roundingMode` default `half_up`
 * (apps/api/prisma/schema.prisma) and the Wheelhouse provider mapper (SCRUM-245),
 * so one mode governs the ingest, pricing, tax, and display paths alike.
 *
 * Exceptions: {@link centsToDollars} is display-only (exact `/100`, no rounding
 * mode — see its JSDoc). Native `+`/`-` on integer cents
 * ({@link addCents}/{@link subtractCents}/{@link sumCents}) is exact and allowed;
 * CLAUDE.md rule #2 bans only native `* / %` on money.
 *
 * @see CLAUDE.md rule #2 (Money is always `Cents`).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import Decimal from 'decimal.js';
/** Create a Cents value from an integer. Throws if not a safe integer. */
export function cents(value) {
    if (!Number.isSafeInteger(value)) {
        throw new Error(`Cents value must be a safe integer, got: ${value}`);
    }
    return value;
}
/** Convert a dollar amount (e.g. 19.99) to Cents (1999). */
export function dollarsToCents(dollars) {
    // Pin ROUND_HALF_UP EXPLICITLY — do not rely on the decimal.js global default,
    // so a future global config change cannot silently alter monetary rounding
    // (mirrors {@link applyBasisPoints}). HALF_UP rounds .5 away from zero.
    const result = new Decimal(dollars)
        .times(100)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
/**
 * Convert Cents to a dollar amount for DISPLAY purposes only.
 *
 * Carries NO rounding mode by design: the only operation is `/100`, which
 * decimal.js computes exactly (a whole-cent integer over 100 is a terminating
 * 2-decimal value — no rounding step occurs). The final `.toNumber()` returns a
 * JS `number`, so the dollar value is subject to IEEE-754 representation like any
 * float — format with `.toFixed(2)` at the serialization boundary, and never feed
 * it back into a monetary write without re-crossing via {@link dollarsToCents}.
 * `Cents` remains the canonical persisted form.
 *
 * Round-trip `centsToDollars`∘`dollarsToCents` is identity on whole-cent dollar
 * values across the empirically-confirmed `[1, 1e11]` range (SCRUM-245).
 */
export function centsToDollars(amount) {
    return new Decimal(amount).dividedBy(100).toNumber();
}
/** Add two Cents values. */
export function addCents(a, b) {
    return cents(a + b);
}
/** Subtract Cents: a - b. */
export function subtractCents(a, b) {
    return cents(a - b);
}
/**
 * Multiply Cents by a factor (e.g. quantity or a multiplier).
 *
 * Uses decimal.js and pins `Decimal.ROUND_HALF_UP` EXPLICITLY — never the
 * decimal.js global default — so monetary rounding cannot be silently altered by
 * a config change elsewhere (see the module-level "Canonical rounding mode"
 * note). HALF_UP rounds `.5` away from zero. A result past the safe-integer
 * range throws via the {@link cents} guard — a deliberate failure, never a
 * silent overflow.
 */
export function multiplyCents(amount, factor) {
    const result = new Decimal(amount)
        .times(factor)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
/**
 * Divide Cents by a divisor (e.g. splitting a bill).
 *
 * Uses decimal.js and pins `Decimal.ROUND_HALF_UP` EXPLICITLY — never the
 * decimal.js global default — so monetary rounding cannot be silently altered by
 * a config change elsewhere (see the module-level "Canonical rounding mode"
 * note). HALF_UP rounds `.5` away from zero. A zero divisor throws rather than
 * returning `Infinity`/`NaN` — a deliberate fail-closed guard.
 */
export function divideCents(amount, divisor) {
    if (divisor === 0) {
        throw new Error('Cannot divide Cents by zero');
    }
    const result = new Decimal(amount)
        .dividedBy(divisor)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
/**
 * Calculate a percentage of a Cents amount; `percentage` is a number (15 = 15%).
 *
 * Uses decimal.js and pins `Decimal.ROUND_HALF_UP` EXPLICITLY — never the
 * decimal.js global default — so monetary rounding cannot be silently altered by
 * a config change elsewhere (see the module-level "Canonical rounding mode"
 * note). HALF_UP rounds `.5` away from zero (25% of 10¢ = 2.5 → 3¢). 100% returns
 * the base exactly. For rates expressed in basis points use {@link applyBasisPoints}.
 */
export function percentOfCents(amount, percentage) {
    const result = new Decimal(amount)
        .times(percentage)
        .dividedBy(100)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
/** Sum an array of Cents values. */
export function sumCents(amounts) {
    return cents(amounts.reduce((acc, val) => acc + val, 0));
}
/**
 * Apply a basis-point rate to a Cents base: `round(base × bp / 10_000, HALF_UP)`.
 *
 * The canonical money primitive for percentage-of-money math expressed in basis
 * points (1% = 100 bp; 100% = 10_000 bp), per the manual tax engine formula
 * (DS-012 Taxes & Fees BC §9.2) and any percentage fee/tax. Replaces the banned
 * native `base * bp / 10_000` (CLAUDE.md rule 2).
 *
 * Rounding is the LAST step only (§10) and pins `ROUND_HALF_UP` EXPLICITLY — it
 * does not rely on the decimal.js global default, so a future global config
 * change cannot silently alter monetary rounding. HALF_UP rounds .5 away from
 * zero (50.5 → 51; -50.5 → -51), matching the design's stated rounding mode.
 *
 * Sign-agnostic like {@link cents}: a negative base (refund/credit) is allowed.
 * An out-of-range or non-finite result throws via the {@link cents} safe-integer
 * guard — a deliberate failure mode, never a silent overflow.
 */
export function applyBasisPoints(base, bp) {
    const result = new Decimal(base)
        .times(bp)
        .dividedBy(10_000)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
/**
 * Provider-call cost: `round_HALF_UP( Σ (tokens_class × rateCentsPerMtok_class) / 1_000_000 )`.
 *
 * SCRUM-355 (DS-016-2): the money primitive behind `AiCallTrace.costCents`.
 * The four per-class products are SUMMED first and rounded EXACTLY ONCE at the
 * end (never per-class) so two sub-cent classes cannot each round up
 * independently (0.5 + 0.5 = 1.0 → 1, not 1 + 1 = 2). The denominator is
 * `1_000_000` (rates are cents-per-million-tokens).
 *
 * Rounding pins `Decimal.ROUND_HALF_UP` EXPLICITLY — never the decimal.js
 * global default — so a future global config change cannot silently shift
 * monetary rounding (identical discipline to {@link applyBasisPoints}).
 *
 * BigInt rates are converted via `new Decimal(rate.toString())` — the clean
 * BigInt→decimal.js boundary — so a large token count × a multi-dollar rate
 * stays exact all the way through the `÷1_000_000`, never overflowing a
 * native `number` mid-computation. The final result crosses the {@link cents}
 * safe-integer guard, which THROWS rather than silently returning an imprecise
 * value when the total exceeds `Number.MAX_SAFE_INTEGER`.
 *
 * Never uses native `*` / `/` on money (CLAUDE.md rule 2).
 */
export function computeCost(usage, rates) {
    const sumProducts = new Decimal(usage.input)
        .times(new Decimal(rates.inputRateCentsPerMtok.toString()))
        .plus(new Decimal(usage.output).times(new Decimal(rates.outputRateCentsPerMtok.toString())))
        .plus(new Decimal(usage.cacheWrite).times(new Decimal(rates.cacheWriteRateCentsPerMtok.toString())))
        .plus(new Decimal(usage.cacheRead).times(new Decimal(rates.cacheReadRateCentsPerMtok.toString())));
    const result = sumProducts
        .dividedBy(1_000_000)
        .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
        .toNumber();
    return cents(result);
}
//# sourceMappingURL=cents.js.map